import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Auth from 'modules/Auth';
import DropdownMenu from 'lib/DropdownMenu';
import { Color } from 'lib/Button';
import {
  PLANNER_URLS,
  useCampaignTypeSwitch,
} from 'components/common/Deal/CampaignHeader/CampaignDrawer/CampaignTypeSwitch/useCampaignTypeSwitch';
import { DropdownMenuPosition } from 'lib/DropdownMenu/DropdownMenu.types';
import programmaticIcon from 'assets/icons/programmatic.svg';
import directSalesIcon from 'assets/icons/direct-sales.svg';
import { IconSize } from 'components/common/types/Input.types';
import { CampaignType } from 'components/pages/Planner/PlannerSections/types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import { PermissionsEnum } from 'consts/permissions';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';

export const SelectTradingType: React.FC = () => {
  const { onChange: onClickCampaignTypeOption } = useCampaignTypeSwitch();
  const { pathname } = useLocation();
  const hasDirectSalesPlannerEnabled = useHasFeatureAccess(FeatureFlags.DIRECT_SALES_PLANNER);
  const isNewDeal = useSelector(getIsNewDeal);

  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);

  const isOptionSelected = (campaignType: CampaignType): boolean => {
    const baseUrl = PLANNER_URLS[campaignType];
    return pathname === baseUrl || pathname.startsWith(`${baseUrl}/`);
  };

  const dropdownMenuOptions = [
    ...(checkFeaturePermission(arePermissionsEnabled, Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION))
      ? [
          {
            id: 'programmatic',
            label: 'Programmatic',
            icon: programmaticIcon,
            isSelected: isOptionSelected(CampaignType.PROGRAMMATIC),
            onClick: () => onClickCampaignTypeOption(CampaignType.PROGRAMMATIC),
          },
        ]
      : []),

    ...(hasDirectSalesPlannerEnabled &&
    checkFeaturePermission(arePermissionsEnabled, Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION))
      ? [
          {
            id: 'direct-sales',
            label: 'Direct sales',
            icon: directSalesIcon,
            isSelected: isOptionSelected(CampaignType.DIRECT),
            onClick: () => onClickCampaignTypeOption(CampaignType.DIRECT),
          },
        ]
      : []),
  ];

  const selectedOption = dropdownMenuOptions.find((option) => option.isSelected) ?? dropdownMenuOptions[0];

  return (
    <div className="w-[340px] flex gap-2 pl-[8px] items-center">
      <DropdownMenu
        label={selectedOption.label}
        color={Color.TRANSPARENT}
        icon={selectedOption.icon}
        iconSize={IconSize.LARGE}
        dropdownMenuPosition={DropdownMenuPosition.LEFT}
        dropdownMenuOptions={dropdownMenuOptions}
        className="!p-1 text-primary focus:bg-primary-200 focus:ring-2 focus:ring-primary-600"
        isDisabled={!isNewDeal}
      />
    </div>
  );
};
